import { getFormProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { format } from 'date-fns/fp';
import { useEffect } from 'react';
import { useFetcher, useParams, data } from 'react-router';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';
import { getClientIPAddress } from 'remix-utils/get-client-ip-address';
import { z } from 'zod';

import { CheckboxField } from '~/components/forms/checkbox-field';
import { TextField } from '~/components/forms/text-field';
import { RichTextHtmlViewer } from '~/components/rich-text/rich-text-html-viewer';
import { Modal } from '~/components/ui/modal';
import { StatusButton } from '~/components/ui/status-button';
import { acceptWaiver } from '~/domain/waivers/handlers/accept-waiver.server';
import { requireSession } from '~/utils/auth.server';
import { validateCSRF } from '~/utils/csrf.server';
import { handleDomainFormError } from '~/utils/errors.server';

import { type Route } from './+types/accept-waiver';

const schema = z.object({
  fullName: z.string(),
  waiverId: z.string(),
  accepted: z.boolean().refine((value) => value === true, { message: 'Required' }),
});

interface Props {
  gymName: string;
  waiverId: string;
  waiverContent: string;
  acceptedAt: string | null;
  acceptedBy: string | null;
  open: boolean;
  onClose: () => void;
}

const formatDate = format("yyyy/MM/dd 'at' HH:mm aa");

export async function action({ request }: Route.ActionArgs) {
  const session = await requireSession(request);
  const formData = await request.formData();

  await validateCSRF(formData, request.headers);

  const submission = parseWithZod(formData, { schema });
  if (submission.status !== 'success') {
    return submission.reply();
  }

  const clientIp = getClientIPAddress(request);

  const acceptResult = await acceptWaiver(session, {
    userId: session.userId,
    waiverId: submission.value.waiverId,
    name: submission.value.fullName,
    ip: clientIp,
  });

  if (acceptResult.isErr()) {
    return handleDomainFormError(acceptResult.error, submission);
  }

  return data(acceptResult);
}

export function WaiverModal({
  onClose,
  waiverContent,
  waiverId,
  open,
  acceptedAt,
  acceptedBy,
  gymName,
}: Props) {
  const { slug } = useParams();
  const fetcher = useFetcher<typeof action>();

  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    constraint: getZodConstraint(schema),
    onValidate: ({ formData }) => parseWithZod(formData, { schema }),
    defaultValue: {
      fullName: acceptedBy && acceptedAt ? `${acceptedBy} (${formatDate(acceptedAt)})` : '',
      waiverId,
      accepted: acceptedAt !== null,
    },
    shouldDirtyConsider: (name) => name !== 'csrf',
  });

  // close the modal when the waiver is accepted
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      onClose();
    }
  }, [fetcher, onClose]);

  const accepted = acceptedAt !== null;

  const isSubmitting = fetcher.state !== 'idle';

  return (
    <Modal
      title={accepted ? gymName : 'Review & Sign Waiver'}
      open={open}
      onClose={onClose}
      dialogContentProps={{
        className: 'overflow-hidden',
      }}
    >
      {accepted ? null : (
        <p className="text-body2">Once signed, the waiver will be saved to your account.</p>
      )}

      <div className="mt-6 rounded border border-black">
        <div className="max-h-[305px] overflow-y-scroll pr-2 text-body2">
          <RichTextHtmlViewer rawHtml={waiverContent} />

          <fetcher.Form
            {...getFormProps(form)}
            method="post"
            className="mt-6 p-4"
            action={`/gym/${slug}/resources/accept-waiver`}
          >
            <AuthenticityTokenInput />
            <input type="hidden" name="waiverId" value={waiverId} />
            <TextField field={fields.fullName} label="Enter Your Name" disabled={accepted} />
            <CheckboxField
              field={fields.accepted}
              label="I have read and agree to the terms of this Waiver."
              checkboxProps={{ disabled: accepted }}
            />

            {accepted ? null : (
              <StatusButton
                className="ml-auto mt-4"
                disabled={isSubmitting || !form.dirty}
                type="submit"
                color="primary"
                status={isSubmitting ? 'pending' : 'idle'}
              >
                Submit
              </StatusButton>
            )}
          </fetcher.Form>
        </div>
      </div>
    </Modal>
  );
}
